import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * PopoutService (legacy v1) — coordinates "open the chat in a separate window"
 * between the embedded iframe (the *opener*) and the popped-out top-level window
 * (the *popout*).
 *
 * Legacy v1 has no conversation persistence, so the popout is **best-effort**: a
 * roomy top-level window that inherits the same-origin auth cookie / user info
 * but starts a fresh conversation. Only the security handshake + freeze/restore
 * coordination are relayed.
 *
 * ## Why window-to-window postMessage (not BroadcastChannel)
 * The chat is usually a *cross-site* iframe, and Chrome partitions storage
 * (incl. `BroadcastChannel`) by top-level site — so the iframe and a top-level
 * popout would be in different partitions and couldn't talk. Direct window
 * messaging via the `window.open` handle and `window.opener` is NOT partitioned.
 *
 * ## Security
 * The opener iframe (which already passed `requireEmbedding`/`requiredReferrers`)
 * is the trust anchor; it hands the popout a one-time `nonce` via the
 * `window.open` target name. Messages are accepted only from the same origin and
 * only when the nonce matches; a direct `/?popout=1` visit has no opener and
 * stays blocked.
 */

const WINDOW_NAME_PREFIX = 'omm_cb_popout_';
const MSG = 'omm_cb_popout_msg';
const SS_NONCE = 'omm_cb_popout_nonce';
const SS_AUTHORIZED = 'omm_cb_popout_authorized';
const SS_PARENT_ORIGIN = 'omm_cb_popout_parent_origin';
const HELLO_RETRY_MS = 600;
const HANDSHAKE_TIMEOUT_MS = 25000;
const CLOSE_POLL_MS = 600;

interface PopoutMessage {
  channel: typeof MSG;
  type: 'hello' | 'init' | 'active' | 'closing';
  nonce: string;
  parentOrigin?: string | null;
}

export interface IPopoutAuthorization {
  parentOrigin: string | null;
}

@Injectable({ providedIn: 'root' })
export class PopoutService {
  /** Whether THIS window is the popped-out window (`?popout=1`). */
  readonly isPopout: boolean;

  /** Opener side: emits when the popout window has closed. */
  readonly closed$ = new Subject<void>();

  private _poppedOut = false;
  /** Opener side: a popout window is currently open (drives the freeze overlay). */
  get poppedOut(): boolean {
    return this._poppedOut;
  }

  // Opener state
  private openerNonce: string | null = null;
  private openerParentOrigin: string | null = null;
  private popoutRef: Window | null = null;
  private closePollTimer: ReturnType<typeof setInterval> | null = null;
  private closedReadings = 0;

  // Popout state
  private authResolve: ((info: IPopoutAuthorization | null) => void) | null = null;
  private authRetryTimer: ReturnType<typeof setInterval> | null = null;
  private authDeadlineTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const params = new URLSearchParams(window.location.search);
    this.isPopout = params.get('popout') === '1' || params.get('popout') === 'true';

    window.addEventListener('message', (e) => this.onMessage(e));

    if (this.isPopout) {
      window.addEventListener('beforeunload', () => {
        const nonce = this.getPopoutNonce();
        if (nonce) this.postTo(window.opener, { type: 'closing', nonce });
      });
    }
  }

  // ─── Opener API ───

  /**
   * Open the chat in a separate top-level window. Returns false if the browser
   * blocked the popup (must be called from a user-gesture handler).
   */
  openPopout(datasetId: string, parentOrigin: string | null): boolean {
    const nonce = this.generateNonce();
    this.openerNonce = nonce;
    this.openerParentOrigin = parentOrigin;

    const url = new URL(`?popout=1&assistant=${encodeURIComponent(datasetId)}`, document.baseURI).href;
    const ref = window.open(url, WINDOW_NAME_PREFIX + nonce, 'width=480,height=720,resizable=yes,scrollbars=yes');
    if (!ref) {
      this.openerNonce = null;
      return false;
    }
    this.popoutRef = ref;
    this._poppedOut = true;
    this.startClosePoll();
    return true;
  }

  /** Focus then close the popout, returning the user to the in-page chat. */
  bringBack(): void {
    try {
      this.popoutRef?.focus();
      this.popoutRef?.close();
    } catch {
      // cross-origin / already closed
    }
    this.handleOpenerClosed();
  }

  // ─── Popout API ───

  /**
   * Resolve authorization for this popped-out window. Returns `{ parentOrigin }`
   * once the opener iframe completes the handshake (or the value restored from
   * sessionStorage after a re-auth redirect). Resolves null when no opener
   * answers within the deadline, so the caller falls back to the normal
   * access-control checks, which still block `requireEmbedding` datasets.
   */
  awaitAuthorization(): Promise<IPopoutAuthorization | null> {
    if (!this.isPopout) return Promise.resolve(null);

    if (sessionStorage.getItem(SS_AUTHORIZED)) {
      return Promise.resolve({ parentOrigin: sessionStorage.getItem(SS_PARENT_ORIGIN) });
    }

    const nonce = this.getPopoutNonce();
    if (!nonce || !window.opener) return Promise.resolve(null);

    return new Promise<IPopoutAuthorization | null>((resolve) => {
      this.authResolve = resolve;
      this.postTo(window.opener, { type: 'hello', nonce });
      this.authRetryTimer = setInterval(() => this.postTo(window.opener, { type: 'hello', nonce }), HELLO_RETRY_MS);
      this.authDeadlineTimer = setTimeout(() => this.settleAuth(null), HANDSHAKE_TIMEOUT_MS);
    });
  }

  /** Resolve the authorization promise once and clear the retry/deadline timers. */
  private settleAuth(value: IPopoutAuthorization | null): void {
    if (this.authRetryTimer) { clearInterval(this.authRetryTimer); this.authRetryTimer = null; }
    if (this.authDeadlineTimer) { clearTimeout(this.authDeadlineTimer); this.authDeadlineTimer = null; }
    const resolve = this.authResolve;
    this.authResolve = null;
    if (resolve) resolve(value);
  }

  // ─── Internals ───

  private onMessage(e: MessageEvent): void {
    if (e.origin !== window.location.origin) return;
    const msg = e.data as PopoutMessage;
    if (!msg || typeof msg !== 'object' || msg.channel !== MSG) return;

    if (!this.isPopout) {
      if (!this.openerNonce || msg.nonce !== this.openerNonce) return;
      if (msg.type === 'hello') {
        this.postTo(e.source as Window | null, { type: 'init', nonce: this.openerNonce, parentOrigin: this.openerParentOrigin });
      } else if (msg.type === 'closing') {
        this.handleOpenerClosed();
      }
      return;
    }

    if (msg.type === 'init') {
      const nonce = this.getPopoutNonce();
      if (!nonce || msg.nonce !== nonce) return;
      this.persistAuthorization(msg.parentOrigin ?? null);
      this.postTo(window.opener, { type: 'active', nonce });
      this.settleAuth({ parentOrigin: msg.parentOrigin ?? null });
    }
  }

  private postTo(target: Window | null, msg: Omit<PopoutMessage, 'channel'>): void {
    try {
      target?.postMessage({ channel: MSG, ...msg }, window.location.origin);
    } catch {
      // target gone / cross-origin
    }
  }

  private getPopoutNonce(): string | null {
    const stored = sessionStorage.getItem(SS_NONCE);
    if (stored) return stored;
    const name = window.name;
    if (name && name.startsWith(WINDOW_NAME_PREFIX)) {
      const nonce = name.slice(WINDOW_NAME_PREFIX.length);
      sessionStorage.setItem(SS_NONCE, nonce);
      return nonce;
    }
    return null;
  }

  private persistAuthorization(parentOrigin: string | null): void {
    sessionStorage.setItem(SS_AUTHORIZED, '1');
    if (parentOrigin) sessionStorage.setItem(SS_PARENT_ORIGIN, parentOrigin);
  }

  private startClosePoll(): void {
    this.stopClosePoll();
    this.closedReadings = 0;
    this.closePollTimer = setInterval(() => {
      if (this.popoutRef && !this.popoutRef.closed) {
        this.closedReadings = 0;
        return;
      }
      if (++this.closedReadings >= 2) {
        this.handleOpenerClosed();
      }
    }, CLOSE_POLL_MS);
  }

  private stopClosePoll(): void {
    if (this.closePollTimer) {
      clearInterval(this.closePollTimer);
      this.closePollTimer = null;
    }
  }

  private handleOpenerClosed(): void {
    if (!this._poppedOut) return;
    this.stopClosePoll();
    this.popoutRef = null;
    this.openerNonce = null;
    this._poppedOut = false;
    this.closed$.next();
  }

  private generateNonce(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return 'p' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
  }
}
